import {
    createStyles,
    FormControl,
    Grid,
    makeStyles,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { useState } from 'react';
import { SiteDto } from '../../../../api/siteApi';
import {
    ConditionOptions,
    createThresholdCondition,
    deleteThresholdCondition, FieldOptions, ThresholdConditionDto, updateThresholdCondition
} from '../../../../api/thresholdConditionApi';
import { EventType, formatTypeForDisplay } from '../../../../enums/eventType';
import { CustomTheme } from '../../../../styles/theme';
import { PopupContainer } from '../../../reusable/popupContainer';
import { selectMenuProp } from '../../exportEvents/exportEventsPage';

const useStyles = makeStyles((theme: CustomTheme) =>
    createStyles({
        popupContent: {
            marginTop: 5,
            color: theme.palette.text.secondary,
        },
        eventString: {
            color: theme.palette.text.disabled,
            paddingBottom: '0.5rem',
            overflow: 'wrap',
        },
        numberInput: {
            width: 75,
        },
        valueSymbol: {
            marginLeft: -15,
            color: theme.palette.text.secondary,
        },
        expandIcons: {
            color: theme.palette.extraColors.iconColor1,
            position: 'absolute',
            right: 0,
            top: 'calc(50% - 12px)',
            pointerEvents: 'none',
        },
    }),
);

export const formatConditionForDisplay = (condition: ConditionOptions, value: number | null) => {
    var conditionDisplay = condition.toString();
    if (
        condition === ConditionOptions.LessThanOrEqual ||
        condition === ConditionOptions.GreaterThanOrEqual ||
        condition === ConditionOptions.Equal
    ) {
        conditionDisplay += 'To';
    }
    conditionDisplay = conditionDisplay.replace(/([A-Z])/g, ' $1').trim() + ' ';
    conditionDisplay = conditionDisplay.toLowerCase();
    conditionDisplay = conditionDisplay.charAt(0).toUpperCase() + conditionDisplay.slice(1);

    if (value === null) {
        return conditionDisplay;
    } else {
        return conditionDisplay + value;
    }
};

export const formatFieldForDisplay = (field: FieldOptions, displaySymbol: boolean) => {
    var fieldDisplay = field.toString();
    fieldDisplay = fieldDisplay.replace(/([A-Z])/g, ' $1').trim() + ' ';
    if (displaySymbol) {
        fieldDisplay += `(${getUnitForField(FieldOptions[field as FieldOptions])})`;
    }
    return fieldDisplay;
};

export const getUnitForField = (field: FieldOptions) => {
    /*if (field === FieldOptions.Power) {
        return 'MW';
    } else if (field === FieldOptions.ReactivePower) {
        return 'var';
    } else */ if (
        field === FieldOptions.Capacity
    ) {
        return '%';
    } else {
        return '';
    }
};

interface ThresholdEventPopupProps {
    handleClose: (newData: boolean) => void;
    currentData: ThresholdConditionDto | null;
    siteList: SiteDto[];
}

export const ThresholdEventPopup = (props: ThresholdEventPopupProps) => {
    const classes = useStyles();
    const expandMore = () => (
        <ExpandMore className={classes.expandIcons} />
    );
    const { handleClose, currentData } = props;
    let dataCopy: ThresholdConditionDto;
    if (currentData === null) {
        dataCopy = {
            siteKey: props.siteList ? props.siteList[0].key : '',
            siteName: props.siteList ? props.siteList[0].name : '',
            eventType: EventType.PpaBreach,
            id: -1,
            field: FieldOptions.Capacity,
            eventCondition: ConditionOptions.LessThanOrEqual,
            minimumDuration: 0,
            value: 0,
        };
    } else {
        dataCopy = currentData;
    }

    const [updatedData, setUpdatedData] = useState(dataCopy);

    const getThresholdConditionStringForDisplay = (eventType: EventType) => {
        return (
            'A ' +
            formatTypeForDisplay(eventType) +
            ' Event will be created for ' +
            updatedData.siteName +
            ' when the ' +
            formatFieldForDisplay(updatedData.field, false).toLowerCase() +
            ' is ' +
            formatConditionForDisplay(
                updatedData.eventCondition,
                +updatedData.value,
            ).toLowerCase() +
            getUnitForField(FieldOptions[updatedData.field as FieldOptions]) +
            ' for a duration of ' +
            updatedData.minimumDuration +
            ' hours'
        );
    };

    return (
        <PopupContainer
            titleText={currentData ? 'Edit Condition' : 'Add Condition'}
            isEditing={currentData ? true : false}
            handleClose={handleClose}
            addFunction={createThresholdCondition}
            updateFunction={updateThresholdCondition}
            deleteFunction={deleteThresholdCondition}
            dataCopy={updatedData}
            getContent={() => {
                return (
                    <Grid container className={classes.popupContent} alignItems="center">
                        <Grid item xs={5}>
                            Site
                        </Grid>
                        <Grid item xs={7}>
                            <FormControl fullWidth={true}>
                                <Select
                                    value={updatedData.siteKey}
                                    onChange={(e) => {
                                        var key = e.target.value as string;
                                        var site = props.siteList.find((e) => e.key === key);
                                        if (site) {
                                            setUpdatedData({
                                                ...updatedData,
                                                siteName: site.name,
                                                siteKey: key,
                                            });
                                        } else {
                                            console.log('Error: site not found');
                                        }
                                    }}
                                    IconComponent={expandMore}
                                    MenuProps={selectMenuProp}
                                >
                                    {updatedData.siteKey !== '' &&
                                        !props.siteList.find(
                                            (site) => site.key === updatedData.siteKey,
                                        ) && (
                                            <MenuItem
                                                key={updatedData.siteKey}
                                                value={updatedData.siteKey}
                                            >
                                                {updatedData.siteName}
                                            </MenuItem>
                                        )}
                                    {props.siteList.map((site) => {
                                        return (
                                            <MenuItem key={site.key} value={site.key}>
                                                {site.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                            Event Type
                        </Grid>
                        <Grid item xs={7}>
                            <FormControl fullWidth={true}>
                                <Select
                                    value={updatedData.eventType}
                                    onChange={(e) => {
                                        setUpdatedData({
                                            ...updatedData,
                                            eventType: EventType[e.target.value as EventType],
                                        });
                                    }}
                                    IconComponent={expandMore}
                                    MenuProps={selectMenuProp}
                                >
                                    {[{ id: 1, eventType: EventType.PpaBreach}, { id: 2, eventType: EventType.RemitBreach}, { id: 3, eventType: EventType.FpnBreach}].map((eventType) => {
                                        return (
                                            <MenuItem key={eventType.id} value={eventType.eventType}>
                                                {
                                                    formatTypeForDisplay(EventType[eventType.eventType])
                                                }
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                            Field
                        </Grid>
                        <Grid item xs={7}>
                            <FormControl fullWidth={true}>
                                <Select
                                    value={updatedData.field}
                                    onChange={(e) => {
                                        setUpdatedData({
                                            ...updatedData,
                                            field: FieldOptions[e.target.value as FieldOptions],
                                        });
                                    }}
                                    IconComponent={expandMore}
                                    MenuProps={selectMenuProp}
                                >
                                    {Object.keys(FieldOptions).map((field, index) => {
                                        return (
                                            <MenuItem key={index} value={field}>
                                                {formatFieldForDisplay(
                                                    FieldOptions[field as FieldOptions],
                                                    true,
                                                )}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                            Condition
                        </Grid>
                        <Grid item xs={7}>
                            <FormControl fullWidth={true}>
                                <Select
                                    value={updatedData.eventCondition}
                                    onChange={(e) => {
                                        setUpdatedData({
                                            ...updatedData,
                                            eventCondition:
                                                ConditionOptions[
                                                    e.target.value as ConditionOptions
                                                ],
                                        });
                                    }}
                                    IconComponent={expandMore}
                                    MenuProps={selectMenuProp}
                                >
                                    {Object.keys(ConditionOptions).map((condition, index) => {
                                        return (
                                            <MenuItem key={index} value={condition}>
                                                {formatConditionForDisplay(
                                                    ConditionOptions[condition as ConditionOptions],
                                                    null,
                                                )}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                            Value
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                className={classes.numberInput}
                                type="number"
                                value={updatedData.value.toString()}
                                onChange={(e) => {
                                    setUpdatedData({
                                        ...updatedData,
                                        value: +(e.target.value as string),
                                    });
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <span className={classes.valueSymbol}>
                                {getUnitForField(FieldOptions[updatedData.field as FieldOptions])}
                            </span>
                        </Grid>
                        <Grid item xs={5}>
                            Minimum Duration
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                className={classes.numberInput}
                                type="number"
                                value={updatedData.minimumDuration.toString()}
                                onChange={(e) => {
                                    setUpdatedData({
                                        ...updatedData,
                                        minimumDuration: +(e.target.value as string),
                                    });
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <span className={classes.valueSymbol}>hours</span>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.eventString}>
                                {getThresholdConditionStringForDisplay(updatedData.eventType)}
                            </div>
                        </Grid>
                    </Grid>
                );
            }}
        />
    );
};
